import Chart from 'chart.js/auto';
import { supabase }            from '../supabase.js';
import { COLORS, CHART_DEFAULTS, rateColor } from '../lib/theme.js';
import { openDrillPanel }      from '../components/drill-panel.js';
import { openEditStudentModal } from '../components/edit-student.js';
import { openSignatureModal }   from '../components/signature-pad.js';
import { hasPart, canChange } from '../lib/access.js';
import { DOC_KINDS, DOC_LABELS, WARNING_TEXT, fmtShort } from '../lib/learner-docs-core.mjs';
import { buildLearnerDocs, downloadPdf, slug } from '../lib/learner-docs.js';

window.__drillCharts = window.__drillCharts || {};

function rateOf(present, total) { return total > 0 ? +((present / total) * 100).toFixed(1) : 0; }
function rateClass(r) { return r >= 75 ? 'badge-green' : r >= 50 ? 'badge-amber' : 'badge-red'; }
function esc(s) { return String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }
function shortName(s, n = 28) { s = String(s ?? ''); return s.length > n ? s.slice(0, n - 1) + '…' : s; }

/* =================================================================
   MUNICIPALITY DRILL
================================================================= */
export async function openMuniDrill({ muniId, muniName, sessionDate }) {
  openDrillPanel({
    eyebrow: 'Municipality detail',
    title:   muniName,
    html:    `<div class="drill-loading">Loading…</div>`,
    chartIds: ['drill-ch-class'],
  });

  const { data, error } = await supabase.rpc('municipality_drill', {
    p_muni_id:      muniId,
    p_session_date: sessionDate || null,
  });
  if (error) {
    document.getElementById('drill-body').innerHTML = `<div class="drill-loading">${esc(error.message)}</div>`;
    return;
  }

  const rate    = rateOf(data.att_present, data.att_present + data.att_absent);
  const classes = data.by_class || [];
  const dateLabel = sessionDate
    ? new Date(sessionDate + 'T00:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
    : 'All time';

  const html = `
    <div class="stat-row">
      <div class="stat-mini"><div class="stat-mini-label">Total Learners</div><div class="stat-mini-value">${data.total_learners.toLocaleString()}</div></div>
      <div class="stat-mini"><div class="stat-mini-label">Active</div><div class="stat-mini-value">${data.active_learners.toLocaleString()}</div></div>
      <div class="stat-mini"><div class="stat-mini-label">Att. Rate</div><div class="stat-mini-value"><span class="${rateClass(rate)}">${rate}%</span></div></div>
      <div class="stat-mini"><div class="stat-mini-label">Records</div><div class="stat-mini-value">${data.att_total.toLocaleString()}</div></div>
    </div>

    <div class="drill-section">
      <div class="drill-section-title">
        Attendance by Class
        <span style="font-weight:400;color:var(--text-muted);font-size:.78rem;margin-left:6px">${esc(dateLabel)}</span>
      </div>
      ${classes.length ? `
        <div class="drill-chart-wrap"><canvas id="drill-ch-class"></canvas></div>
        <div style="margin-top:12px">
          ${classes.map(c => {
            const cr = rateOf(c.present, c.present + c.absent);
            return `
              <div class="drill-venue-row clickable-row"
                   data-class-id="${esc(c.class_id)}" data-class-name="${esc(c.class_name || '')}"
                   style="cursor:pointer">
                <div class="drill-venue-left">
                  <span class="drill-venue-name">${esc(shortName(c.class_name || 'Unnamed class'))}</span>
                  <span class="drill-venue-sub">${c.present} present · ${c.total} records</span>
                </div>
                <div class="drill-venue-right">
                  <span class="${rateClass(cr)}">${cr}%</span>
                  <div class="progress-bar" style="width:70px"><div class="progress-fill" style="width:${cr}%;background:${rateColor(cr)}"></div></div>
                </div>
              </div>`;
          }).join('')}
        </div>
      ` : `<p style="font-size:.82rem;color:var(--text-muted)">No in-class attendance recorded for this date.</p>`}
    </div>
  `;

  openDrillPanel({
    eyebrow: 'Municipality detail',
    title:   muniName,
    html,
    chartIds: ['drill-ch-class'],
    onMount: (body) => {
      if (!classes.length) return;

      const labels = classes.map(c => shortName(c.class_name || 'Unnamed', 22));
      const rates  = classes.map(c => rateOf(c.present, c.present + c.absent));
      window.__drillCharts['drill-ch-class'] = new Chart(document.getElementById('drill-ch-class'), {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            data: rates,
            backgroundColor: rates.map(r => rateColor(r)),
            borderRadius: 5,
            borderSkipped: false,
          }]
        },
        options: {
          ...CHART_DEFAULTS,
          plugins: { ...CHART_DEFAULTS.plugins, legend: { display: false } },
          scales: { y: { min: 0, max: 100, ticks: { callback: v => v + '%' } } },
        }
      });

      body.querySelectorAll('.clickable-row').forEach(row => {
        row.addEventListener('click', () => {
          openClassDrill({ classId: row.dataset.classId, className: row.dataset.className });
        });
      });
    }
  });
}

/* =================================================================
   PROGRAMME DRILL
================================================================= */
export async function openProgDrill({ programmeId, programmeName }) {
  openDrillPanel({
    eyebrow: 'Programme detail',
    title:   programmeName,
    html:    `<div class="drill-loading">Loading…</div>`,
    chartIds: ['drill-ch-trend'],
  });

  const { data, error } = await supabase.rpc('programme_drill', { p_programme_id: programmeId });
  if (error) {
    document.getElementById('drill-body').innerHTML = `<div class="drill-loading">${esc(error.message)}</div>`;
    return;
  }

  const rate = rateOf(data.att_present, data.att_present + data.att_absent);

  const html = `
    <div class="stat-row">
      <div class="stat-mini"><div class="stat-mini-label">Learners</div><div class="stat-mini-value">${data.total_learners.toLocaleString()}</div></div>
      <div class="stat-mini"><div class="stat-mini-label">Records</div><div class="stat-mini-value">${data.att_total.toLocaleString()}</div></div>
      <div class="stat-mini"><div class="stat-mini-label">Rate</div><div class="stat-mini-value"><span class="${rateClass(rate)}">${rate}%</span></div></div>
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Monthly Attendance Trend</div>
      <div class="drill-chart-wrap"><canvas id="drill-ch-trend"></canvas></div>
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Facilitators (${data.facilitators.length})</div>
      ${data.facilitators.length ? [...data.facilitators].sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' })).map(name => `
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
          <div style="width:28px;height:28px;border-radius:50%;background:var(--purple);color:#fff;display:flex;align-items:center;justify-content:center;font-size:.7rem;font-weight:700">${esc(initials(name))}</div>
          <span style="font-size:.85rem">${esc(name)}</span>
        </div>
      `).join('') : '<p style="font-size:.82rem;color:var(--text-muted)">No facilitator assigned yet.</p>'}
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Overall Performance</div>
      <div style="display:flex;align-items:center;gap:12px;margin-bottom:8px">
        <div style="font-family:'Syne',sans-serif;font-size:2.5rem;font-weight:800;color:${rateColor(rate)}">${rate}%</div>
        <div style="font-size:.82rem;color:var(--text-muted)">${data.att_present.toLocaleString()} present<br>${data.att_absent.toLocaleString()} absent</div>
      </div>
      <div class="progress-bar" style="height:8px">
        <div class="progress-fill" style="width:${rate}%;background:${rateColor(rate)}"></div>
      </div>
    </div>
  `;

  openDrillPanel({
    eyebrow: 'Programme detail',
    title:   programmeName,
    html,
    chartIds: ['drill-ch-trend'],
    onMount: () => {
      const months  = data.monthly.map(m => m.month);
      const present = data.monthly.map(m => m.present);
      const absent  = data.monthly.map(m => m.absent);
      window.__drillCharts['drill-ch-trend'] = new Chart(document.getElementById('drill-ch-trend'), {
        type: 'line',
        data: {
          labels: months,
          datasets: [
            { label: 'Present', data: present, borderColor: COLORS.green, backgroundColor: 'rgba(16,185,129,0.1)',
              fill: true, tension: 0.4, borderWidth: 2, pointRadius: 3 },
            { label: 'Absent',  data: absent,  borderColor: COLORS.red,   backgroundColor: 'rgba(239,68,68,0.07)',
              fill: true, tension: 0.4, borderWidth: 2, pointRadius: 3 },
          ]
        },
        options: { ...CHART_DEFAULTS,
          plugins: { ...CHART_DEFAULTS.plugins,
            legend: { display: true, position: 'top',
              labels: { color: '#64748B', font: { family: 'DM Sans', size: 10 }, boxWidth: 8, padding: 6 } } }
        }
      });
    }
  });
}

/* =================================================================
   FACILITATOR DRILL
================================================================= */
export async function openFacDrill({ facilitatorId, facilitatorName }) {
  openDrillPanel({
    eyebrow: 'Facilitator detail',
    title:   facilitatorName,
    html:    `<div class="drill-loading">Loading…</div>`,
    chartIds: [],
  });

  const { data, error } = await supabase.rpc('facilitator_drill', { p_facilitator_id: facilitatorId });
  if (error) {
    document.getElementById('drill-body').innerHTML = `<div class="drill-loading">${esc(error.message)}</div>`;
    return;
  }

  const rate = rateOf(data.att_present, data.att_present + data.att_absent);

  const html = `
    <div class="stat-row">
      <div class="stat-mini"><div class="stat-mini-label">Classes</div><div class="stat-mini-value">${data.classes_count}</div></div>
      <div class="stat-mini"><div class="stat-mini-label">Learners reached</div><div class="stat-mini-value">${data.learners_reached.toLocaleString()}</div></div>
      <div class="stat-mini"><div class="stat-mini-label">Att. Rate</div><div class="stat-mini-value"><span class="${rateClass(rate)}">${rate}%</span></div></div>
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Per-class Attendance</div>
      ${data.by_class.length ? data.by_class.map(c => {
        const cr = rateOf(c.present, c.present + c.absent);
        return `
          <div class="drill-venue-row clickable-row" data-class-id="${esc(c.class_id)}" data-class-name="${esc(c.class_name || '')}">
            <div class="drill-venue-left">
              <span class="drill-venue-name">${esc(shortName(c.class_name, 36))}</span>
              <span class="drill-venue-sub">${c.present} present · ${c.total} records</span>
            </div>
            <div class="drill-venue-right">
              <span class="${rateClass(cr)}">${cr}%</span>
              <div class="progress-bar" style="width:70px"><div class="progress-fill" style="width:${cr}%;background:${rateColor(cr)}"></div></div>
            </div>
          </div>
        `;
      }).join('') : '<p style="font-size:.82rem;color:var(--text-muted)">No classes assigned to this facilitator.</p>'}
    </div>
  `;

  openDrillPanel({
    eyebrow: 'Facilitator detail',
    title:   facilitatorName,
    html,
    chartIds: [],
    onMount: (body) => {
      body.querySelectorAll('.clickable-row').forEach(row => {
        row.style.cursor = 'pointer';
        row.addEventListener('click', () => {
          openClassDrill({ classId: row.dataset.classId, className: row.dataset.className });
        });
      });
    },
  });
}

function initials(name) {
  return String(name || '').split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

/* =================================================================
   LEARNING STAGE HELPERS
================================================================= */
const LEARNING_STAGES = [
  { key: 'in_class',             label: 'In Class' },
  { key: 'poe_submission',       label: 'POE Submission' },
  { key: 'assessment',           label: 'Assessment' },
  { key: 'moderation',           label: 'Moderation' },
  { key: 'practical_experience', label: 'Practical Exp.' },
  { key: 'certification',        label: 'Certification' },
];

function stageStepper(enrollmentId, currentStage, isAdmin) {
  const activeIdx = LEARNING_STAGES.findIndex(s => s.key === (currentStage || 'in_class'));
  return `
    <div class="stage-stepper" style="display:flex;flex-wrap:wrap;gap:3px;margin-top:6px;padding-top:6px;border-top:1px solid var(--border-light)">
      ${LEARNING_STAGES.map((s, i) => {
        const done   = i < activeIdx;
        const active = i === activeIdx;
        const bg     = done ? '#10B981' : active ? '#6366F1' : '#E2E8F0';
        const fg     = (done || active) ? '#fff' : '#94A3B8';
        const cursor = isAdmin ? 'pointer' : 'default';
        return `<span
          class="stage-pill"
          data-stage-key="${esc(s.key)}"
          data-enrollment-id="${esc(enrollmentId)}"
          style="background:${bg};color:${fg};padding:2px 8px;border-radius:20px;font-size:.7rem;font-weight:600;cursor:${cursor};user-select:none"
          title="${esc(isAdmin ? 'Set stage: ' + s.label : s.label)}"
        >${esc(s.label)}${active ? ' ←' : ''}</span>`;
      }).join('')}
    </div>`;
}

function stageLabel(key) {
  return LEARNING_STAGES.find(s => s.key === key)?.label || (key ? key : 'In Class');
}

/* =================================================================
   STUDENT DRILL
================================================================= */
export async function openStudentDrill({ studentUuid, studentName }) {
  openDrillPanel({
    eyebrow: 'Learner detail',
    title:   studentName || '',
    html:    `<div class="drill-loading">Loading…</div>`,
    chartIds: [],
  });

  const [studentRes, enrolRes, sigRes] = await Promise.all([
    supabase.from('students').select('*').eq('student_uuid', studentUuid).single(),
    supabase.from('class_roster')
      .select('enrollment_id,class_id,class_name,programme_id,programme_name,training_venue_name,enrollment_status,enrollment_date,completion_date,learning_stage')
      .eq('student_uuid', studentUuid)
      .order('enrollment_date', { ascending: false }),
    supabase.from('student_signatures')
      .select('sig_id,programme_id,signature_url,captured_at,programmes(programme_name)')
      .eq('student_uuid', studentUuid),
  ]);

  if (studentRes.error) {
    document.getElementById('drill-body').innerHTML = `<div class="drill-loading">${esc(studentRes.error.message)}</div>`;
    return;
  }
  const s          = studentRes.data;
  const enrolments = enrolRes.data || [];
  const signatures = sigRes.data   || [];

  const [muniRes, progRes] = await Promise.all([
    s.municipality_id
      ? supabase.from('municipalities').select('municipality_name').eq('municipality_id', s.municipality_id).single()
      : Promise.resolve({ data: null }),
    s.programme_id
      ? supabase.from('programmes').select('programme_name').eq('programme_id', s.programme_id).single()
      : Promise.resolve({ data: null }),
  ]);
  const muniName = muniRes.data?.municipality_name || '—';
  const progName = progRes.data?.programme_name    || '—';

  const fullName = [s.first_name, s.surname || s.last_name].filter(Boolean).join(' ') || studentName || s.national_id;
  const dob      = s.date_of_birth ? new Date(s.date_of_birth).toLocaleDateString('en-ZA') : '—';
  const enrolled = enrolments.filter(e => e.enrollment_status === 'enrolled');
  const isAdmin  = hasPart('learners');   // every learner's full record + training stage

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-ZA') : '—';

  const html = `
    <div style="display:flex;justify-content:flex-end;margin-bottom:10px">
      ${canChange('learners') ? '<button id="std-edit-btn" class="btn btn-primary" style="font-size:.82rem;padding:6px 16px">✏️ Edit Learner</button>' : ''}
    </div>

    <div class="stat-row" style="align-items:flex-start">
      <div class="stat-mini"><div class="stat-mini-label">Status</div><div class="stat-mini-value"><span class="badge ${statusBadge(s.status)}">${esc(statusLabel(s.status))}</span></div></div>
      <div class="stat-mini"><div class="stat-mini-label">Enrolments</div><div class="stat-mini-value">${enrolled.length}</div></div>
      <div class="stat-mini"><div class="stat-mini-label">Gender</div><div class="stat-mini-value" style="font-size:1rem">${esc(s.gender || '—')}</div></div>
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Bio</div>
      ${kvRow('ID Number',   s.national_id)}
      ${kvRow('Full name',   fullName)}
      ${kvRow('Date of birth', dob)}
      ${kvRow('Race',        s.race)}
      ${kvRow('Disability',  s.has_disability ? (s.disability_type || 'Yes') : 'None')}
      ${kvRow('Highest qualification', s.highest_qualification)}
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Contact</div>
      ${kvRow('Email',   s.email)}
      ${kvRow('Phone',   s.phone_number)}
      ${kvRow('Address', s.residential_address)}
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Programme & Location</div>
      ${kvRow('Programme',    progName)}
      ${kvRow('Municipality', muniName)}
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Employment Info</div>
      ${kvRow('Employee Number', s.employee_number)}
      ${kvRow('UIF Ref Number',  s.uif_ref_number)}
      ${kvRow('Tax Ref Number',  s.tax_ref_number)}
      ${kvRow('Start Date',       fmtDate(s.start_date))}
      ${kvRow('Termination Date', fmtDate(s.termination_date))}
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Banking</div>
      ${kvRow('Bank',            s.bank_name)}
      ${kvRow('Account',         s.bank_account_number)}
      ${kvRow('UIF contributor', s.uif_contributor ? 'Yes' : 'No')}
    </div>

    ${window.__access?.learnerPay ? `
    <div class="drill-section" id="std-uif-docs">
      <div class="drill-section-title">UIF documents</div>
      <p style="font-size:.78rem;color:var(--text-muted);margin:0 0 8px">Confirmation letter, salary schedule and UI-19, with the current values. The cohort and period come from the class, or from a period correction in Payslips &amp; documents › Document settings.</p>
      <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center">
        <select id="std-uif-kind" class="select-input" style="font-size:.8rem" aria-label="Which document">
          <option value="">All of them (one PDF)</option>
          ${DOC_KINDS.map(k => `<option value="${k}">${esc(DOC_LABELS[k])}</option>`).join('')}
        </select>
        <button id="std-uif-dl" class="btn btn-primary" style="font-size:.8rem;padding:5px 14px">Download PDF</button>
      </div>
      <p id="std-uif-msg" style="font-size:.78rem;margin:6px 0 0"></p>
    </div>` : ''}

    <div class="drill-section" id="std-docs-section">
      <div class="drill-section-title" style="display:flex;justify-content:space-between;align-items:center">
        <span>Documents</span>
        <button id="std-doc-upload-btn" class="btn btn-ghost" style="font-size:.75rem;padding:3px 10px">+ Upload</button>
      </div>
      ${(() => {
        const files = Array.isArray(s.other_files) ? s.other_files : [];
        if (!files.length) return `<p style="font-size:.82rem;color:var(--text-muted)">No documents uploaded yet.</p>`;
        return files.map((f, idx) => `
          <div style="display:flex;justify-content:space-between;align-items:center;padding:5px 0;border-bottom:1px solid var(--border-light);font-size:.82rem">
            <a href="${esc(f.url)}" target="_blank" style="color:var(--link);text-decoration:underline">${esc(f.name || 'Document')}</a>
            <button class="btn btn-ghost std-doc-del-btn" data-doc-idx="${idx}" style="font-size:.72rem;padding:2px 8px;color:var(--danger)">✕</button>
          </div>`).join('');
      })()}
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Stored Signatures (${signatures.length})</div>
      ${signatures.length ? signatures.map(sig => {
        const progName = sig.programmes?.programme_name || sig.programme_id;
        const capDate  = sig.captured_at ? new Date(sig.captured_at).toLocaleDateString('en-ZA') : '—';
        return `
          <div class="drill-venue-row" style="flex-direction:column;align-items:stretch;gap:6px" data-sig-id="${esc(sig.sig_id)}" data-programme-id="${esc(sig.programme_id)}">
            <div style="display:flex;justify-content:space-between;align-items:center">
              <div>
                <span class="drill-venue-name" style="font-size:.82rem">${esc(progName)}</span>
                <span class="drill-venue-sub">Captured ${capDate}</span>
              </div>
              <div style="display:flex;gap:6px">
                <button class="btn btn-ghost sig-recapture-btn" data-sig-id="${esc(sig.sig_id)}" data-programme-id="${esc(sig.programme_id)}" style="font-size:.75rem;padding:3px 10px">Re-capture</button>
                <button class="btn btn-danger sig-remove-btn"   data-sig-id="${esc(sig.sig_id)}" data-programme-id="${esc(sig.programme_id)}" style="font-size:.75rem;padding:3px 10px">Remove</button>
              </div>
            </div>
            <img src="${esc(sig.signature_url)}" alt="Stored signature" style="border:1px solid var(--border-light);border-radius:6px;max-height:60px;max-width:100%;object-fit:contain;background:#fff">
          </div>`;
      }).join('') : `<p style="font-size:.82rem;color:var(--text-muted)">No stored signatures yet. Mark a learner present in the Attendance view to capture their signature.</p>`}
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Class enrolments (${enrolments.length})</div>
      ${enrolments.length ? enrolments.map(e => `
        <div class="drill-venue-row" style="flex-direction:column;align-items:stretch">
          <div style="display:flex;justify-content:space-between;align-items:flex-start">
            <div class="drill-venue-left">
              <span class="drill-venue-name">${esc(shortName(e.class_name || '—', 36))}</span>
              <span class="drill-venue-sub">${esc(e.programme_name || '—')}${e.training_venue_name ? ' · ' + esc(e.training_venue_name) : ''}</span>
            </div>
            <div class="drill-venue-right">
              <span class="badge ${e.enrollment_status === 'enrolled' ? 'badge-green' : 'badge-gray'}">${esc(e.enrollment_status)}</span>
            </div>
          </div>
          ${stageStepper(e.enrollment_id, e.learning_stage, isAdmin)}
        </div>
      `).join('') : '<p style="font-size:.82rem;color:var(--text-muted)">Not enrolled in any class yet.</p>'}
    </div>
  `;

  openDrillPanel({
    eyebrow: 'Learner detail',
    title:   fullName,
    html,
    chartIds: [],
    onMount: (body) => {
      /* Edit button */
      body.querySelector('#std-edit-btn')?.addEventListener('click', () => {
        openEditStudentModal({
          studentUuid,
          onSave: () => openStudentDrill({ studentUuid, studentName: fullName }),
        });
      });

      /* UIF documents — learner-pay holders only */
      body.querySelector('#std-uif-dl')?.addEventListener('click', async (ev) => {
        const btn  = ev.currentTarget;
        const out  = body.querySelector('#std-uif-msg');
        const kind = body.querySelector('#std-uif-kind').value;
        btn.disabled = true; out.style.color = 'var(--text-muted)'; out.textContent = 'Building…';
        try {
          const { bytes, docs, skipped } = await buildLearnerDocs([studentUuid], {
            kinds: kind ? [kind] : undefined,
          });
          if (!bytes) { out.style.color = 'var(--red)'; out.textContent = skipped[0]?.reason || 'Nothing to print.'; return; }
          const d = docs[0];
          downloadPdf(bytes, `${slug(d.full_name)}-${kind || 'uif-documents'}.pdf`);
          const notes = d.warnings.map(w => WARNING_TEXT[w] || w);
          out.style.color = notes.length ? 'var(--orange-dark)' : '#15803D';
          out.textContent = `Downloaded (${d.cohort_name}, ${fmtShort(d.period_start)} – ${fmtShort(d.period_end)}).${notes.length ? ' Check: ' + notes.join('; ') + '.' : ''}`;
        } catch (e) {
          out.style.color = 'var(--red)'; out.textContent = e.message;
        } finally { btn.disabled = false; }
      });

      /* Stage stepper — admin only */
      if (isAdmin) {
        body.querySelectorAll('.stage-pill[data-enrollment-id]').forEach(pill => {
          pill.addEventListener('click', async () => {
            const enrollmentId = pill.dataset.enrollmentId;
            const stageKey     = pill.dataset.stageKey;
            pill.style.opacity = '0.5';
            const { error } = await supabase
              .from('student_class_enrollments')
              .update({ learning_stage: stageKey })
              .eq('enrollment_id', enrollmentId);
            if (error) { alert(error.message); pill.style.opacity = '1'; return; }
            openStudentDrill({ studentUuid, studentName: fullName });
          });
        });
      }

      /* Signature re-capture buttons */
      body.querySelectorAll('.sig-recapture-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          const { programmeId } = btn.dataset;
          const blob = await openSignatureModal({ title: `New signature for ${fullName}` });
          if (!blob) return;
          const termPath = `terms/${programmeId}/${studentUuid}.png`;
          const up = await supabase.storage.from('signatures').upload(termPath, blob, { upsert: true, contentType: 'image/png' });
          if (up.error) { alert(`Upload failed: ${up.error.message}`); return; }
          const { data: { publicUrl } } = supabase.storage.from('signatures').getPublicUrl(termPath);
          const { data: { user } } = await supabase.auth.getUser();
          await supabase.from('student_signatures').upsert(
            { student_uuid: studentUuid, programme_id: programmeId, signature_url: publicUrl, captured_by: user?.id ?? null },
            { onConflict: 'student_uuid,programme_id' }
          );
          openStudentDrill({ studentUuid, studentName: fullName });
        });
      });

      /* Signature remove buttons */
      body.querySelectorAll('.sig-remove-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          if (!confirm('Remove stored signature? The next mark-present will prompt for a new one.')) return;
          const { sigId, programmeId } = btn.dataset;

          // 1. Delete the stored signature record
          await supabase.from('student_signatures').delete().eq('sig_id', sigId);

          // 2. Clear signature_url from any attendance rows that used this signature
          //    (handles cases where the wrong person signed in this learner's block)
          await supabase
            .from('attendance')
            .update({ signature_url: null })
            .eq('student_uuid', studentUuid);

          // 3. Remove the image file from storage
          await supabase.storage.from('signatures').remove([`terms/${programmeId}/${studentUuid}.png`]);

          openStudentDrill({ studentUuid, studentName: fullName });
        });
      });

      /* Profile document upload */
      body.querySelector('#std-doc-upload-btn')?.addEventListener('click', () => {
        const input = document.createElement('input');
        input.type   = 'file';
        input.accept = 'application/pdf,image/jpeg,image/png,image/webp';
        input.click();
        input.addEventListener('change', async () => {
          const file = input.files[0];
          if (!file) return;
          if (file.size > 10 * 1024 * 1024) { alert('File must be under 10 MB.'); return; }
          const ext  = file.name.split('.').pop().toLowerCase();
          const ts   = Date.now();
          const path = `students/${studentUuid}/${ts}.${ext}`;
          const up   = await supabase.storage.from('documents').upload(path, file, { upsert: false, contentType: file.type });
          if (up.error) { alert(`Upload failed: ${up.error.message}`); return; }
          const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(path);
          const currentFiles = Array.isArray(s.other_files) ? s.other_files : [];
          const newFiles = [...currentFiles, { name: file.name, url: publicUrl, uploaded_at: new Date().toISOString() }];
          const { error } = await supabase.from('students').update({ other_files: newFiles }).eq('student_uuid', studentUuid);
          if (error) { alert(`Save failed: ${error.message}`); return; }
          openStudentDrill({ studentUuid, studentName: fullName });
        });
      });

      /* Profile document delete */
      body.querySelectorAll('.std-doc-del-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          if (!confirm('Remove this document?')) return;
          const idx          = parseInt(btn.dataset.docIdx, 10);
          const currentFiles = Array.isArray(s.other_files) ? s.other_files : [];
          const newFiles     = currentFiles.filter((_, i) => i !== idx);
          await supabase.from('students').update({ other_files: newFiles }).eq('student_uuid', studentUuid);
          openStudentDrill({ studentUuid, studentName: fullName });
        });
      });
    },
  });
}

/* =================================================================
   CLASS DRILL
================================================================= */
export async function openClassDrill({ classId, className }) {
  openDrillPanel({
    eyebrow: 'Class detail',
    title:   className,
    html:    `<div class="drill-loading">Loading…</div>`,
    chartIds: [],
  });

  const [classRes, rosterRes] = await Promise.all([
    supabase.from('classes').select('*').eq('class_id', classId).single(),
    supabase.from('class_roster')
      .select('enrollment_id,student_uuid,national_id,first_name,surname,programme_name,training_venue_name,municipality_name,enrollment_status,enrollment_date,learning_stage')
      .eq('class_id', classId)
      .order('first_name', { ascending: true })
      .order('surname',    { ascending: true }),
  ]);

  if (classRes.error) {
    document.getElementById('drill-body').innerHTML = `<div class="drill-loading">${esc(classRes.error.message)}</div>`;
    return;
  }
  const c = classRes.data;
  const roster = rosterRes.data || [];
  const enrolled = roster.filter(r => r.enrollment_status === 'enrolled');

  const [progRes, facRes] = await Promise.all([
    c.programme_id
      ? supabase.from('programmes').select('programme_name').eq('programme_id', c.programme_id).single()
      : Promise.resolve({ data: null }),
    c.facilitator_id
      ? supabase.from('facilitators').select('full_name').eq('facilitator_id', c.facilitator_id).single()
      : Promise.resolve({ data: null }),
  ]);

  const progName  = progRes.data?.programme_name
                 ?? roster[0]?.programme_name ?? '—';
  const venueName = roster[0]?.training_venue_name ?? '—';
  const muniName  = roster[0]?.municipality_name ?? '—';
  const facName   = facRes.data?.full_name || 'Unassigned';

  const startDate = c.start_date ? new Date(c.start_date).toLocaleDateString('en-ZA') : '—';
  const endDate   = c.end_date   ? new Date(c.end_date).toLocaleDateString('en-ZA')   : '—';
  const fillPct   = c.capacity ? Math.min(100, Math.round((enrolled.length / c.capacity) * 100)) : 0;

  const html = `
    <div class="stat-row">
      <div class="stat-mini"><div class="stat-mini-label">Enrolled</div><div class="stat-mini-value">${enrolled.length}<span style="font-size:.7rem;color:var(--text-muted)"> / ${c.capacity ?? '—'}</span></div></div>
      <div class="stat-mini"><div class="stat-mini-label">Status</div><div class="stat-mini-value"><span class="badge ${c.status === 'active' ? 'badge-green' : 'badge-gray'}">${esc(c.status || '—')}</span></div></div>
      <div class="stat-mini"><div class="stat-mini-label">Capacity</div><div class="stat-mini-value">${fillPct}%</div></div>
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Class info</div>
      ${kvRow('Programme',    progName)}
      ${kvRow('Venue',        venueName)}
      ${kvRow('Municipality', muniName)}
      ${kvRow('Facilitator',  facName)}
      ${kvRow('Starts',       startDate)}
      ${kvRow('Ends',         endDate)}
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Training Progress</div>
      ${(() => {
        const counts = {};
        LEARNING_STAGES.forEach(s => { counts[s.key] = 0; });
        enrolled.forEach(r => { const k = r.learning_stage || 'in_class'; if (counts[k] !== undefined) counts[k]++; });
        return `<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:2px">
          ${LEARNING_STAGES.map(s => counts[s.key] > 0 ? `
            <span style="background:#F1F5F9;border-radius:16px;padding:3px 10px;font-size:.75rem;font-weight:600;color:var(--text-muted)">
              ${esc(s.label)}: <strong style="color:var(--text-main)">${counts[s.key]}</strong>
            </span>` : '').join('')}
        </div>`;
      })()}
    </div>

    <div class="drill-section">
      <div class="drill-section-title">Roster — enrolled (${enrolled.length})</div>
      ${enrolled.length ? enrolled.map(r => {
        const fname = [r.first_name, r.surname].filter(Boolean).join(' ') || r.national_id;
        const slabel = stageLabel(r.learning_stage);
        return `
          <div class="drill-venue-row clickable-row" data-student-uuid="${esc(r.student_uuid)}" data-student-name="${esc(fname)}">
            <div class="drill-venue-left">
              <span class="drill-venue-name">${esc(shortName(fname, 34))}</span>
              <span class="drill-venue-sub">${esc(r.national_id)}</span>
            </div>
            <div class="drill-venue-right" style="flex-direction:column;align-items:flex-end;gap:3px">
              <span style="color:var(--text-muted);font-size:.78rem">View →</span>
              <span style="background:#6366F1;color:#fff;padding:1px 7px;border-radius:20px;font-size:.67rem;font-weight:600">${esc(slabel)}</span>
            </div>
          </div>
        `;
      }).join('') : '<p style="font-size:.82rem;color:var(--text-muted)">No enrolled learners in this class.</p>'}
    </div>
  `;

  openDrillPanel({
    eyebrow: 'Class detail',
    title:   className,
    html,
    chartIds: [],
    onMount: (body) => {
      body.querySelectorAll('.clickable-row').forEach(row => {
        row.style.cursor = 'pointer';
        row.addEventListener('click', () => {
          openStudentDrill({
            studentUuid: row.dataset.studentUuid,
            studentName: row.dataset.studentName,
          });
        });
      });
    },
  });
}

function kvRow(label, value) {
  return `
    <div style="display:flex;justify-content:space-between;gap:12px;padding:6px 0;border-bottom:1px solid var(--border-light);font-size:.82rem">
      <span style="color:var(--text-muted)">${esc(label)}</span>
      <span style="text-align:right">${esc(value || '—')}</span>
    </div>
  `;
}

function statusBadge(s) {
  if (s === 'active')      return 'badge-green';
  if (s === 'withdrawn')   return 'badge-red';
  if (s === 'transferred') return 'badge-gray';
  if (s === 'graduated')   return 'badge-blue';
  if (s === 'completed')   return 'badge-blue';
  return 'badge-gray';
}

function statusLabel(s) {
  if (s === 'withdrawn') return 'Dropout';
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
}
